import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    ComposedChart,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";
import { Download } from "lucide-react";
import { api, fetchBlob } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import ScoreBadge from "../components/ScoreBadge";
import ImageModal from "../components/ImageModal";
import LoadingSpinner from "../components/LoadingSpinner";
import ReviewDashboard from "./ReviewDashboard";

const chartTooltipStyle = {
    backgroundColor: "#1e2130",
    border: "1px solid #2a2d3a",
    borderRadius: "8px",
    color: "#e8eaf0",
    fontSize: "13px",
};

const SEVERITY_COLORS = {
    critical: "#ef4444",
    warning: "#f97316",
    info: "#3b82f6",
};

function StoreListCard({ stores }) {
    const [filter, setFilter] = useState("issues");

    const withIssues = stores.filter(s => s.openIssues > 0);
    const withScans = stores.filter(s => s.lastScanAt);
    const displayStores = filter === "issues" ? withIssues
        : filter === "scans" ? withScans : stores;

    return (
        <div className="card">
            <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Stores ({displayStores.length})</span>
                <div style={{ display: "flex", gap: 0 }}>
                    {[
                        { key: "issues", label: `Issues (${withIssues.length})` },
                        { key: "scans", label: `Scanned (${withScans.length})` },
                        { key: "all", label: `All (${stores.length})` },
                    ].map((m) => (
                        <button
                            key={m.key}
                            onClick={() => setFilter(m.key)}
                            style={{
                                padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                border: "1px solid var(--border)",
                                borderRight: m.key !== "all" ? "none" : undefined,
                                borderRadius: m.key === "issues" ? "5px 0 0 5px" : m.key === "all" ? "0 5px 5px 0" : "0",
                                background: filter === m.key ? "var(--primary)" : "var(--bg-hover)",
                                color: filter === m.key ? "#fff" : "var(--text-secondary)",
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>
            {displayStores.length > 0 ? (
                <table className="store-table">
                    <thead>
                        <tr><th>Store</th><th>Score</th><th>Issues</th><th>Last Scan</th></tr>
                    </thead>
                    <tbody>
                        {displayStores.map((store) => (
                            <tr key={store.siteId}>
                                <td>
                                    <Link to={`/store/${store.siteId}`} className="store-link">
                                        <div className="store-name">{store.name}</div>
                                        <div className="store-id">#{store.siteId} {"\u2014"} {store.state}</div>
                                    </Link>
                                </td>
                                <td><ScoreBadge score={store.latestScore} /></td>
                                <td style={{ color: store.openIssues > 0 ? "#ef4444" : "var(--text-muted)" }}>
                                    {store.openIssues || 0}
                                </td>
                                <td style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                                    {store.lastScanAt ? new Date(store.lastScanAt).toLocaleString("en-AU", { timeZone: "Australia/Melbourne" }) : "\u2014"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="empty-state"><p>No stores in this filter</p></div>
            )}
        </div>
    );
}

function AwaitingRoleBanner() {
    const { user } = useAuth();
    if (!user || !user.roles) return null;
    const isDefaultViewer = user.roles.length === 1 && user.roles[0].key === "viewer";
    if (!isDefaultViewer) return null;
    return (
        <div style={{
            background: "linear-gradient(135deg, #1e3a5f, #1a1d2e)",
            border: "1px solid #3b82f6",
            borderRadius: 8,
            padding: "20px 24px",
            marginBottom: 20,
            color: "#e8eaf0",
        }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Welcome to Store Audit</div>
            <p style={{ margin: 0, color: "#94a3b8", fontSize: 14, lineHeight: 1.5 }}>
                An email has been sent to your administrator to allocate you a role.
                You will be notified when your role is assigned. In the meantime, you have
                read-only access to the dashboard and stores list.
            </p>
        </div>
    );
}

function OpenIssuesSection({ issues, loadData, filterSeverity, setFilterSeverity, filterTeam, setFilterTeam, filterFixture, setFilterFixture, filterSite, setFilterSite, sortBy, setSortBy }) {
    const teams = useMemo(() => [...new Set(issues.map(i => i.assignedToTeam).filter(Boolean))].sort(), [issues]);
    const fixtures = useMemo(() => [...new Set(issues.map(i => i.fixtureType).filter(Boolean))].sort(), [issues]);
    const severities = useMemo(() => [...new Set(issues.map(i => i.severity).filter(Boolean))], [issues]);

    const filtered = useMemo(() => {
        return issues
            .filter(i => {
                if (filterSite && !(i.siteId || "").toString().includes(filterSite)) return false;
                if (filterSeverity && i.severity !== filterSeverity) return false;
                if (filterTeam && i.assignedToTeam !== filterTeam) return false;
                if (filterFixture && i.fixtureType !== filterFixture) return false;
                return true;
            })
            .sort((a, b) => {
                if (sortBy === "newest") return new Date(b.lastDetectedAt || 0) - new Date(a.lastDetectedAt || 0);
                if (sortBy === "oldest") return new Date(a.lastDetectedAt || 0) - new Date(b.lastDetectedAt || 0);
                if (sortBy === "severity") {
                    const order = { critical: 0, high: 1, warning: 2, medium: 3, low: 4, info: 5 };
                    return (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
                }
                return 0;
            });
    }, [issues, filterSite, filterSeverity, filterTeam, filterFixture, sortBy]);

    return (
        <div className="card" style={{ marginTop: 24 }}>
            <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Open Issues ({filtered.length}{filtered.length !== issues.length ? ` of ${issues.length}` : ""})</span>
                <Link to="/issues" style={{ color: "var(--primary)", fontWeight: 600, fontSize: 12 }}>View All</Link>
            </div>
            <div className="filter-row" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8, display: "flex" }}>
                <input
                    type="text"
                    className="filter-select"
                    placeholder="Site ID..."
                    value={filterSite}
                    onChange={(e) => setFilterSite(e.target.value)}
                    style={{ minWidth: 100, fontSize: 12, padding: "5px 8px" }}
                />
                <select className="filter-select" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All Severities</option>
                    {severities.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="filter-select" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All Teams</option>
                    {teams.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className="filter-select" value={filterFixture} onChange={(e) => setFilterFixture(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All Fixtures</option>
                    {fixtures.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="severity">By Severity</option>
                </select>
            </div>
            {filtered.length > 0 ? (
                <div className="issues-list">
                    {filtered.slice(0, 20).map((issue) => (
                        <IssueCard key={issue.id} issue={issue} onAction={loadData} />
                    ))}
                    {filtered.length > 20 && (
                        <div style={{ textAlign: "center", padding: 12 }}>
                            <Link to="/issues" style={{ color: "var(--primary)", fontWeight: 600, fontSize: 13 }}>
                                View all {filtered.length} issues
                            </Link>
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-state"><p>No open issues{issues.length > 0 ? " matching filters" : ""}</p></div>
            )}
        </div>
    );
}

function Dashboard() {
    const [view, setView] = useState("operational");
    const [data, setData] = useState(null);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groupBy, setGroupBy] = useState(null);
    const [groupMonth, setGroupMonth] = useState("");
    const [exporting, setExporting] = useState(false);
    const [groupedData, setGroupedData] = useState(null);
    const [groupedLoading, setGroupedLoading] = useState(false);
    const [grpSort, setGrpSort] = useState({ field: "outstanding", dir: "desc" });
    const [grpFilter, setGrpFilter] = useState({ rpl: "", state: "" });
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [groupTrend, setGroupTrend] = useState(null);
    const [groupTrendLoading, setGroupTrendLoading] = useState(false);
    const [datePeriod, setDatePeriod] = useState(null);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [trendPeriod, setTrendPeriod] = useState("month");
    const [trends, setTrends] = useState(null);
    const [trendsLoading, setTrendsLoading] = useState(false);
    const [hourlyStore, setHourlyStore] = useState("");
    const [hourlyDate, setHourlyDate] = useState(new Date().toISOString().slice(0, 10));
    const [hourlyImages, setHourlyImages] = useState(null);
    const [hourlyLoading, setHourlyLoading] = useState(false);
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);

    // Unified date-window params: custom range > period pill > month picker > all-time.
    const dateParams = useCallback(() => {
        if (dateFrom || dateTo) { const p = {}; if (dateFrom) p.from = dateFrom; if (dateTo) p.to = dateTo; return p; }
        if (datePeriod) return { period: datePeriod };
        if (groupMonth) return { month: groupMonth };
        return {};
    }, [datePeriod, groupMonth, dateFrom, dateTo]);

    const loadData = useCallback(async () => {
        try {
            const dp = dateParams();
            const [dashboard, storeList] = await Promise.all([
                api.dashboard(Object.keys(dp).length ? dp : undefined),
                api.stores(),
            ]);
            setData(dashboard);
            setStores(storeList);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [dateParams]);

    // Download the current grouped view as an Excel workbook
    const downloadGroupExcel = useCallback(async () => {
        if (!groupBy) return;
        setExporting(true);
        try {
            const params = new URLSearchParams({ groupBy, ...dateParams() });
            const blob = await fetchBlob(`/dashboard/national/export.xlsx?${params.toString()}`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const label = groupBy === "rpl" ? "RPL" : groupBy === "state" ? "State" : groupBy === "team" ? "Team" : groupBy === "severity" ? "Severity" : "Site";
            const tag = (dateFrom || dateTo) ? `${dateFrom || "start"}_to_${dateTo || "now"}` : (datePeriod || groupMonth || "all-time");
            a.download = `StoreAudit_By${label}_${tag}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            // swallow — export is best-effort; the on-screen table is still available
        } finally {
            setExporting(false);
        }
    }, [groupBy, dateParams, dateFrom, dateTo, datePeriod, groupMonth]);

    // Load grouped data when groupBy or the date window changes
    useEffect(() => {
        if (!groupBy) { setGroupedData(null); return; }
        setGroupedLoading(true);
        setExpandedGroup(null);
        setGroupTrend(null);
        api.dashboard({ groupBy, ...dateParams() })
            .then((res) => setGroupedData(res))
            .catch(() => setGroupedData(null))
            .finally(() => setGroupedLoading(false));
    }, [groupBy, dateParams]);

    // Load the trend when expanding a group row — granularity matches the period.
    useEffect(() => {
        if (!expandedGroup || !groupBy) { setGroupTrend(null); return; }
        setGroupTrendLoading(true);
        api.dashboardGroupTrend(groupBy, expandedGroup, datePeriod)
            .then((d) => setGroupTrend(d))
            .catch(() => setGroupTrend(null))
            .finally(() => setGroupTrendLoading(false));
    }, [expandedGroup, groupBy, datePeriod]);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 60000);
        return () => clearInterval(interval);
    }, [loadData]);

    // Load operational trends
    useEffect(() => {
        setTrendsLoading(true);
        api.dashboardTrends(trendPeriod)
            .then(d => setTrends(d))
            .catch(() => setTrends(null))
            .finally(() => setTrendsLoading(false));
    }, [trendPeriod]);

    // Load hourly images when store/date selected
    useEffect(() => {
        if (!hourlyStore) { setHourlyImages(null); return; }
        setHourlyLoading(true);
        api.hourlyImages(hourlyStore, hourlyDate)
            .then((d) => setHourlyImages(d))
            .catch(() => setHourlyImages(null))
            .finally(() => setHourlyLoading(false));
    }, [hourlyStore, hourlyDate]);

    if (loading) return <LoadingSpinner message="Loading dashboard..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load dashboard</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={loadData}>Retry</button>
            </div>
        );
    }

    const severityPieData = data?.severityDistribution
        ? Object.entries(data.severityDistribution)
            .filter(([, v]) => v > 0)
            .map(([key, value]) => ({ name: key, value }))
        : [];

    const categoryBarData = (data?.categoryBreakdown || []).map((c) => ({
        category: c.category,
        critical: c.critical,
        warning: c.warning,
    }));

    const trendData = (data?.dailyTrend || []).map((t) => ({
        date: typeof t.date === "string" ? t.date.slice(5) : t.date,
        assessments: t.assessments,
        issues: t.issues,
    }));

    // Executive view cost projections
    const costPerIssue = 120;
    const estMonthlyCost = (data?.periodFunnel?.outstanding ?? data?.openIssues ?? 0) * costPerIssue;

    return (
        <div>
            <AwaitingRoleBanner />
            <div className="page-header">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                        <h2>Dashboard</h2>
                        <p>Network compliance overview</p>
                    </div>
                    <div className="view-toggle">
                        <button
                            className={`view-toggle-btn ${view === "operational" ? "active" : ""}`}
                            onClick={() => setView("operational")}
                        >
                            Operational
                        </button>
                        <button
                            className={`view-toggle-btn ${view === "executive" ? "active" : ""}`}
                            onClick={() => setView("executive")}
                        >
                            Executive
                        </button>
                        <button
                            className={`view-toggle-btn ${view === "review" ? "active" : ""}`}
                            onClick={() => setView("review")}
                        >
                            Review
                        </button>
                    </div>
                </div>
            </div>

            {/* Review dashboard view */}
            {view === "review" && <ReviewDashboard />}

            {view !== "review" && (<>
            {/* Date period filter: day/week/month/year */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Period:</span>
                {[
                    { key: null, label: "All Time" },
                    { key: "day", label: "Today" },
                    { key: "week", label: "This Week" },
                    { key: "month", label: "This Month" },
                    { key: "year", label: "This Year" },
                ].map((p) => (
                    <button
                        key={p.key || "all"}
                        onClick={() => { setDatePeriod(p.key); setGroupMonth(""); setDateFrom(""); setDateTo(""); }}
                        style={{
                            padding: "5px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                            borderRadius: 12,
                            border: (datePeriod === p.key && !groupMonth && !dateFrom && !dateTo) ? "1px solid var(--primary)" : "1px solid var(--border)",
                            background: (datePeriod === p.key && !groupMonth && !dateFrom && !dateTo) ? "var(--primary)" : "var(--bg-hover)",
                            color: (datePeriod === p.key && !groupMonth && !dateFrom && !dateTo) ? "#fff" : "var(--text-secondary)",
                        }}
                    >
                        {p.label}
                    </button>
                ))}
                <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, marginLeft: 8 }}>or Custom:</span>
                <input type="date" className="filter-select" value={dateFrom} title="From date"
                    onChange={(e) => { setDateFrom(e.target.value); setDatePeriod(null); setGroupMonth(""); }}
                    style={{ fontSize: 11, minWidth: 130 }} />
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>to</span>
                <input type="date" className="filter-select" value={dateTo} title="To date"
                    onChange={(e) => { setDateTo(e.target.value); setDatePeriod(null); setGroupMonth(""); }}
                    style={{ fontSize: 11, minWidth: 130 }} />
                {(dateFrom || dateTo) && (
                    <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                        style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer" }}>Clear</button>
                )}
            </div>

            {/* Group-by toggle pills + month filter */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 0 }}>
                    {[
                        { key: null, label: "Network" },
                        { key: "rpl", label: "By RPL" },
                        { key: "state", label: "By State" },
                        { key: "team", label: "By Team" },
                        { key: "site", label: "By Site" },
                        { key: "severity", label: "By Severity" },
                    ].map((m, i, arr) => (
                        <button
                            key={m.key || "network"}
                            onClick={() => setGroupBy(m.key)}
                            style={{
                                padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                border: "1px solid var(--border)",
                                borderRight: i < arr.length - 1 ? "none" : undefined,
                                borderRadius: i === 0 ? "6px 0 0 6px" : i === arr.length - 1 ? "0 6px 6px 0" : "0",
                                background: groupBy === m.key ? "var(--primary)" : "var(--bg-hover)",
                                color: groupBy === m.key ? "#fff" : "var(--text-secondary)",
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
                {groupBy && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>or Month:</span>
                        <input
                            type="month"
                            className="filter-select"
                            value={groupMonth}
                            onChange={(e) => { setGroupMonth(e.target.value); setDatePeriod(null); setDateFrom(""); setDateTo(""); }}
                            style={{ fontSize: 12, minWidth: 150 }}
                        />
                        {groupMonth && (
                            <button
                                onClick={() => setGroupMonth("")}
                                style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer" }}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Grouped view */}
            {groupBy && (
                <div className="card" style={{ marginBottom: 24 }}>
                    <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>{groupBy === "rpl" ? "By RPL" : groupBy === "state" ? "By State" : groupBy === "team" ? "By Team" : groupBy === "severity" ? "By Severity" : "By Site"}</span>
                        <button
                            onClick={downloadGroupExcel}
                            disabled={exporting || !(groupedData?.groups?.length > 0)}
                            title="Download this table as an Excel file"
                            style={{
                                display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                                fontSize: 12, fontWeight: 600, borderRadius: 6, border: "1px solid var(--border)",
                                background: "var(--bg-hover)", color: "var(--text-secondary)",
                                cursor: (exporting || !(groupedData?.groups?.length > 0)) ? "default" : "pointer",
                                opacity: (exporting || !(groupedData?.groups?.length > 0)) ? 0.6 : 1,
                            }}
                        >
                            <Download size={14} />
                            {exporting ? "Preparing..." : "Download xlsx"}
                        </button>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
                        Counts reflect the selected period. Raised / Actioned / Closed are events in the period; Outstanding = raised in the period and still open.
                    </div>
                    {/* RPL/State filter for site view */}
                    {groupBy === "site" && (
                        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                            <input type="text" className="filter-select" placeholder="Filter RPL..." value={grpFilter.rpl}
                                onChange={(e) => setGrpFilter(f => ({ ...f, rpl: e.target.value }))}
                                style={{ fontSize: 12, minWidth: 140 }} />
                            <input type="text" className="filter-select" placeholder="Filter State..." value={grpFilter.state}
                                onChange={(e) => setGrpFilter(f => ({ ...f, state: e.target.value }))}
                                style={{ fontSize: 12, minWidth: 100 }} />
                        </div>
                    )}
                    {groupedLoading ? (
                        <LoadingSpinner message="Loading grouped data..." />
                    ) : groupedData?.groups?.length > 0 ? (() => {
                        const SortTh = ({ label, field }) => (
                            <th style={{ cursor: "pointer", userSelect: "none" }}
                                onClick={() => setGrpSort(prev => ({ field, dir: prev.field === field && prev.dir === "desc" ? "asc" : "desc" }))}>
                                {label} {grpSort.field === field ? (grpSort.dir === "desc" ? "\u25BC" : "\u25B2") : ""}
                            </th>
                        );
                        let filtered = [...groupedData.groups];
                        if (groupBy === "site" && grpFilter.state) {
                            const q = grpFilter.state.toLowerCase();
                            filtered = filtered.filter(g => (g.stateCode || "").toLowerCase().includes(q));
                        }
                        if (groupBy === "site" && grpFilter.rpl) {
                            const q = grpFilter.rpl.toLowerCase();
                            filtered = filtered.filter(g => (g.rplName || "").toLowerCase().includes(q));
                        }
                        const sorted = filtered.sort((a, b) => {
                            const va = a[grpSort.field] ?? -1, vb = b[grpSort.field] ?? -1;
                            return grpSort.dir === "desc" ? (vb > va ? 1 : -1) : (va > vb ? 1 : -1);
                        });
                        return (<>
                        <table className="store-table">
                            <thead>
                                <tr>
                                    <SortTh label={groupBy === "rpl" ? "RPL" : groupBy === "state" ? "State" : groupBy === "team" ? "Team" : groupBy === "severity" ? "Severity" : "Site"} field="name" />
                                    <SortTh label="Raised" field="raised" />
                                    <SortTh label="Actioned" field="actioned" />
                                    <SortTh label="Closed" field="closed" />
                                    <SortTh label="Reopened" field="reopened" />
                                    <SortTh label="Outstanding" field="outstanding" />
                                    <SortTh label="Avg Resolve" field="avgResolveDays" />
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((g) => (
                                    <React.Fragment key={g.name}>
                                    <tr onClick={() => setExpandedGroup(expandedGroup === g.name ? null : g.name)} style={{ cursor: "pointer" }}>
                                        <td>
                                            <span style={{ fontSize: 10, marginRight: 6, color: "var(--text-muted)" }}>{expandedGroup === g.name ? "\u25BC" : "\u25B6"}</span>
                                            <span style={{ fontWeight: 600 }}>{g.displayName || g.name}</span>
                                            {g.displayName && g.displayName !== g.name && (
                                                <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 6 }}>#{g.name}</span>
                                            )}
                                        </td>
                                        <td style={{ color: g.raised > 0 ? "var(--text-secondary)" : "var(--text-muted)" }}>
                                            {g.raised}
                                        </td>
                                        <td style={{ color: g.actioned > 0 ? "#f97316" : "var(--text-muted)" }}>
                                            {g.actioned}
                                        </td>
                                        <td style={{ color: g.closed > 0 ? "#22c55e" : "var(--text-muted)" }}>
                                            {g.closed}
                                        </td>
                                        <td style={{ color: g.reopened > 0 ? "#f97316" : "var(--text-muted)" }}>
                                            {g.reopened}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            {g.outstanding > 0 && ["site", "team", "rpl", "state"].includes(groupBy) ? (
                                                <Link
                                                    to={
                                                        groupBy === "site" ? `/issues?siteId=${encodeURIComponent(g.name)}`
                                                        : groupBy === "team" ? `/issues?assignee=${encodeURIComponent(g.name)}`
                                                        : groupBy === "rpl" ? `/issues?rpl=${encodeURIComponent(g.name)}`
                                                        : `/issues?state=${encodeURIComponent(g.name)}`
                                                    }
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="View outstanding issues"
                                                    style={{ color: "#ef4444", fontWeight: 600, textDecoration: "underline" }}
                                                >
                                                    {g.outstanding}
                                                </Link>
                                            ) : (
                                                <span style={{ color: g.outstanding > 0 ? "#ef4444" : "var(--text-muted)" }}>{g.outstanding}</span>
                                            )}
                                        </td>
                                        <td style={{ color: g.avgResolveDays != null ? (g.avgResolveDays > 7 ? "#ef4444" : g.avgResolveDays > 3 ? "#f97316" : "#22c55e") : "var(--text-muted)" }}>
                                            {g.avgResolveDays != null ? `${g.avgResolveDays}d` : "\u2014"}
                                        </td>
                                    </tr>
                                    {expandedGroup === g.name && (
                                        <tr><td colSpan={7} style={{ padding: "8px 16px", background: "var(--bg-secondary)" }}>
                                            {groupTrendLoading ? (
                                                <div style={{ padding: 8, color: "var(--text-muted)", fontSize: 12 }}>Loading trend...</div>
                                            ) : groupTrend?.weeks?.length > 0 ? (
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 }}>
                                                        {groupTrend?.bucket === "day" ? "Daily Trend (14 days)" : "Trend"}
                                                    </div>
                                                    <ResponsiveContainer width="100%" height={160}>
                                                        <ComposedChart data={groupTrend.weeks.map(w => ({ week: w.week.slice(5), opened: w.opened, closed: w.closed, cumulative: w.cumulative }))}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                                                            <XAxis dataKey="week" stroke="#5c6078" fontSize={10} />
                                                            <YAxis stroke="#5c6078" fontSize={10} allowDecimals={false} />
                                                            <Tooltip contentStyle={chartTooltipStyle} />
                                                            <Bar dataKey="opened" fill="#ef4444" name="Opened" barSize={14} radius={[2, 2, 0, 0]} />
                                                            <Bar dataKey="closed" fill="#22c55e" name="Closed" barSize={14} radius={[2, 2, 0, 0]} />
                                                        </ComposedChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            ) : (
                                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No trend data for this period</div>
                                            )}
                                        </td></tr>
                                    )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        </>);
                    })() : (
                        <div className="empty-state"><p>No data for this grouping</p></div>
                    )}
                </div>
            )}

            {/* Stats row */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-label">Stores Scanned</div>
                    <div className="stat-value">{data?.scannedStores || 0}</div>
                    <div className="stat-change neutral">of {data?.activeStores || 0} active</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Total Scans</div>
                    <div className="stat-value">{(data?.totalScans || 0).toLocaleString()}</div>
                    <div className="stat-change neutral">{(data?.totalAssessments || 0).toLocaleString()} images</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Avg AI Score</div>
                    <div className="stat-value">{data?.avgScore ?? "\u2014"}<span style={{ fontSize: 14, color: "var(--text-muted)" }}>/10</span></div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Issues{datePeriod ? " (period)" : ""}</div>
                    <div className="stat-value" style={{ color: (data?.periodFunnel?.outstanding || 0) > 0 ? "#ef4444" : "var(--text-secondary)" }}>{data?.periodFunnel?.outstanding ?? data?.openIssues ?? 0} <span style={{ fontSize: 14, color: "var(--text-muted)" }}>outstanding</span></div>
                    <div className="stat-change neutral">
                        {data?.periodFunnel?.raised || 0} raised / {data?.periodFunnel?.actioned || 0} actioned / {data?.periodFunnel?.closed || 0} closed
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Today</div>
                    <div className="stat-value">{data?.scansToday || 0} <span style={{ fontSize: 14, color: "var(--text-muted)" }}>scans</span></div>
                    <div className="stat-change neutral">
                        {data?.raisedToday || 0} raised / {data?.actionedToday || 0} actioned
                    </div>
                </div>
            </div>

            {view === "executive" && (
                <div className="stats-grid" style={{ marginBottom: 16 }}>
                    <div className="stat-card">
                        <div className="stat-label">Est. Monthly Cost Impact</div>
                        <div className="stat-value" style={{ color: "var(--orange)" }}>
                            ${estMonthlyCost.toLocaleString()}
                        </div>
                        <div className="stat-change neutral">@ ${costPerIssue}/issue avg</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Network Coverage</div>
                        <div className="stat-value">
                            {((data?.activeStores || 0) / 350 * 100).toFixed(1)}%
                        </div>
                        <div className="stat-change neutral">{data?.activeStores || 0} of 350 sites</div>
                    </div>
                </div>
            )}

            {/* Charts row */}
            <div className="grid-2">
                <div className="card">
                    <div className="card-title">Daily Assessments (14 days)</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                            <XAxis dataKey="date" stroke="#5c6078" fontSize={12} />
                            <YAxis stroke="#5c6078" fontSize={12} />
                            <Tooltip contentStyle={chartTooltipStyle} />
                            <Line
                                type="monotone"
                                dataKey="assessments"
                                stroke="#3b82f6"
                                strokeWidth={2}
                                dot={{ fill: "#3b82f6", r: 3 }}
                                name="Assessments"
                            />
                            <Line
                                type="monotone"
                                dataKey="issues"
                                stroke="#f97316"
                                strokeWidth={2}
                                dot={{ fill: "#f97316", r: 3 }}
                                name="Issues"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="card">
                    <div className="card-title">Issues by Category</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={categoryBarData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" horizontal={false} />
                            <XAxis type="number" stroke="#5c6078" fontSize={12} />
                            <YAxis
                                dataKey="category"
                                type="category"
                                width={90}
                                stroke="#5c6078"
                                fontSize={12}
                            />
                            <Tooltip contentStyle={chartTooltipStyle} />
                            <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Critical" barSize={16} radius={[0, 0, 0, 0]} />
                            <Bar dataKey="warning" stackId="a" fill="#f97316" name="Warning" barSize={16} radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid-2">
                {/* Severity donut */}
                <div className="card">
                    <div className="card-title">Severity Distribution</div>
                    {severityPieData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={severityPieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={80}
                                    dataKey="value"
                                    nameKey="name"
                                    label={({ name, value }) => `${name}: ${value}`}
                                    labelLine={false}
                                >
                                    {severityPieData.map((entry) => (
                                        <Cell
                                            key={entry.name}
                                            fill={SEVERITY_COLORS[entry.name] || "#5c6078"}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={chartTooltipStyle} />
                                <Legend
                                    verticalAlign="bottom"
                                    formatter={(value) => <span style={{ color: "#8b8fa3", fontSize: 12 }}>{value}</span>}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="empty-state"><p>No issues data</p></div>
                    )}
                </div>

            </div>

            {/* Issue pipeline stats */}
            {(data?.issuesCreatedThisWeek > 0 || data?.issuesResolvedThisWeek > 0 || Object.keys(data?.issuesByTeam || {}).length > 0) && (
                <>
                    <div className="stats-grid" style={{ marginTop: 16 }}>
                        <div className="stat-card">
                            <div className="stat-label">Issues This Week</div>
                            <div className="stat-value">{data?.issuesCreatedThisWeek || 0}</div>
                            <div className="stat-change neutral">created</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Resolved This Week</div>
                            <div className="stat-value">{data?.issuesResolvedThisWeek || 0}</div>
                            <div className="stat-change positive">closed</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Avg Issue Age</div>
                            <div className="stat-value" style={{
                                color: (data?.avgIssueAgeDays || 0) > 7 ? "#ef4444" : (data?.avgIssueAgeDays || 0) > 3 ? "#f97316" : "#22c55e"
                            }}>
                                {data?.avgIssueAgeDays || 0}d
                            </div>
                            <div className="stat-change neutral">
                                oldest: {data?.oldestUnresolvedDays || 0}d
                            </div>
                        </div>
                    </div>

                    <div className="grid-2" style={{ marginTop: 16 }}>
                        {/* Issues by Fixture Type (Focus Areas) */}
                        {Object.keys(data?.issuesByFixture || {}).length > 0 && (
                            <div className="card">
                                <div className="card-title">Focus Areas (Issues by Fixture)</div>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart
                                        data={Object.entries(data.issuesByFixture).map(([k, v]) => ({ fixture: k, count: v })).sort((a, b) => b.count - a.count)}
                                        layout="vertical"
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" horizontal={false} />
                                        <XAxis type="number" stroke="#5c6078" fontSize={12} />
                                        <YAxis dataKey="fixture" type="category" width={110} stroke="#5c6078" fontSize={11} />
                                        <Tooltip contentStyle={chartTooltipStyle} />
                                        <Bar dataKey="count" fill="#8b5cf6" barSize={14} radius={[0, 4, 4, 0]} name="Issues" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Issue Aging Distribution */}
                        {data?.issueAging && (
                            <div className="card">
                                <div className="card-title">Issue Aging</div>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={[
                                        { range: "< 1 day", count: data.issueAging.under1Day, fill: "#22c55e" },
                                        { range: "1-3 days", count: data.issueAging.days1to3, fill: "#3b82f6" },
                                        { range: "4-7 days", count: data.issueAging.days4to7, fill: "#f97316" },
                                        { range: "7+ days", count: data.issueAging.over7Days, fill: "#ef4444" },
                                    ]}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                                        <XAxis dataKey="range" stroke="#5c6078" fontSize={12} />
                                        <YAxis stroke="#5c6078" fontSize={12} allowDecimals={false} />
                                        <Tooltip contentStyle={chartTooltipStyle} />
                                        <Bar dataKey="count" name="Issues" barSize={32} radius={[4, 4, 0, 0]}>
                                            {[
                                                { fill: "#22c55e" },
                                                { fill: "#3b82f6" },
                                                { fill: "#f97316" },
                                                { fill: "#ef4444" },
                                            ].map((entry, index) => (
                                                <Cell key={index} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    {/* Issues by Team */}
                    {Object.keys(data?.issuesByTeam || {}).length > 0 && (
                        <div className="card" style={{ marginTop: 16 }}>
                            <div className="card-title">Issues by Team</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "8px 0" }}>
                                {Object.entries(data.issuesByTeam).sort((a, b) => b[1] - a[1]).map(([team, count]) => (
                                    <div key={team} style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        padding: "8px 16px", borderRadius: 8,
                                        background: "var(--bg-secondary)", border: "1px solid var(--border)"
                                    }}>
                                        <span style={{ fontWeight: 600, fontSize: 18, color: "var(--primary)" }}>{count}</span>
                                        <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{team}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Weekly Issue Trend */}
            {data?.weeklyIssueTrend?.length > 0 && (
                <div className="card" style={{ marginTop: 24 }}>
                    <div className="card-title">Weekly Issue Trend (8 weeks)</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data.weeklyIssueTrend.map(w => ({
                            week: w.week ? w.week.slice(5) : "",
                            opened: w.opened,
                            closed: w.closed,
                        }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                            <XAxis dataKey="week" stroke="#5c6078" fontSize={12} />
                            <YAxis stroke="#5c6078" fontSize={12} allowDecimals={false} />
                            <Tooltip contentStyle={chartTooltipStyle} />
                            <Bar dataKey="opened" fill="#ef4444" name="Opened" barSize={20} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="closed" fill="#22c55e" name="Closed" barSize={20} radius={[4, 4, 0, 0]} />
                            <Legend
                                verticalAlign="bottom"
                                formatter={(value) => <span style={{ color: "#8b8fa3", fontSize: 12 }}>{value}</span>}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Infrastructure & Operational Trends */}
            <div className="card" style={{ marginTop: 24 }}>
                <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Infrastructure</span>
                    <div style={{ display: "flex", gap: 0 }}>
                        {[
                            { key: "month", label: "This Month (daily)" },
                            { key: "year", label: "This Year (weekly)" },
                        ].map((m, i, arr) => (
                            <button
                                key={m.key}
                                onClick={() => setTrendPeriod(m.key)}
                                style={{
                                    padding: "4px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                    border: "1px solid var(--border)",
                                    borderRight: i < arr.length - 1 ? "none" : undefined,
                                    borderRadius: i === 0 ? "5px 0 0 5px" : "0 5px 5px 0",
                                    background: trendPeriod === m.key ? "var(--primary)" : "var(--bg-hover)",
                                    color: trendPeriod === m.key ? "#fff" : "var(--text-secondary)",
                                }}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>
                {trendsLoading ? (
                    <LoadingSpinner message="Loading trends..." />
                ) : trends ? (<>
                    {/* Infrastructure summary cards */}
                    {trends.summary && (
                        <div className="stats-grid" style={{ marginBottom: 16 }}>
                            <div className="stat-card">
                                <div className="stat-label">Total Sites</div>
                                <div className="stat-value">{trends.summary.totalStores}</div>
                                <div className="stat-change neutral">{trends.summary.onboardedSites} onboarded</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">NVR Sites</div>
                                <div className="stat-value">{trends.summary.nvrSites}</div>
                                <div className="stat-change neutral">{trends.summary.nvrDevices} devices</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">PTZ Sites</div>
                                <div className="stat-value">{trends.summary.ptzSites}</div>
                                <div className="stat-change neutral">{trends.summary.ptzDevices} devices</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">NVR Online</div>
                                <div className="stat-value" style={{ color: "#22c55e" }}>{trends.summary.nvrOnline}</div>
                                <div className="stat-change negative">
                                    {trends.summary.nvrAuthFail} auth fail, {trends.summary.nvrUnreachable} unreachable
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="grid-2">
                        {/* Onboarding Progress */}
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 }}>
                                Onboarded Sites & Presets
                            </div>
                            {trends.onboarding?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={trends.onboarding.map(d => ({ ...d, date: d.date.slice(5) }))}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                                        <XAxis dataKey="date" stroke="#5c6078" fontSize={11} />
                                        <YAxis yAxisId="left" stroke="#3b82f6" fontSize={11} />
                                        <YAxis yAxisId="right" orientation="right" stroke="#22c55e" fontSize={11} />
                                        <Tooltip contentStyle={chartTooltipStyle} />
                                        <Line yAxisId="left" type="monotone" dataKey="sites" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} name="Sites" />
                                        <Line yAxisId="right" type="monotone" dataKey="presets" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} name="Presets" />
                                        <Legend formatter={(value) => <span style={{ color: "#8b8fa3", fontSize: 12 }}>{value}</span>} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="empty-state"><p>No onboarding data</p></div>
                            )}
                        </div>

                        {/* Camera Health — distinct sites with issues */}
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 }}>
                                Camera Issues (sites affected per {trendPeriod === "year" ? "week" : "day"})
                            </div>
                            {trends.cameraHealth?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={trends.cameraHealth.map(d => ({ ...d, date: d.date.slice(5) }))}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                                        <XAxis dataKey="date" stroke="#5c6078" fontSize={11} />
                                        <YAxis stroke="#5c6078" fontSize={11} />
                                        <Tooltip contentStyle={chartTooltipStyle} />
                                        <Line type="monotone" dataKey="nvr_auth" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} name="NVR Auth Fail" />
                                        <Line type="monotone" dataKey="nvr_unreach" stroke="#f97316" strokeWidth={2} dot={{ r: 2 }} name="NVR Unreachable" />
                                        <Line type="monotone" dataKey="ptz_auth" stroke="#e879f9" strokeWidth={2} dot={{ r: 2 }} name="PTZ Auth Fail" />
                                        <Line type="monotone" dataKey="ptz_unreach" stroke="#fbbf24" strokeWidth={2} dot={{ r: 2 }} name="PTZ Unreachable" />
                                        <Legend formatter={(value) => <span style={{ color: "#8b8fa3", fontSize: 12 }}>{value}</span>} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="empty-state"><p>No camera health data</p></div>
                            )}
                        </div>
                    </div>
                </>) : (
                    <div className="empty-state"><p>Failed to load trends</p></div>
                )}
            </div>

            {/* Hourly Images */}
            <div className="card" style={{ marginTop: 24 }}>
                <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Hourly Images</span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select
                            className="filter-select"
                            value={hourlyStore}
                            onChange={(e) => setHourlyStore(e.target.value)}
                            style={{ fontSize: 12 }}
                        >
                            <option value="">Select store...</option>
                            {stores.filter(s => s.lastScanAt).map(s => (
                                <option key={s.siteId} value={s.siteId}>{s.name} (#{s.siteId})</option>
                            ))}
                        </select>
                        {hourlyStore && (
                            <input
                                type="date"
                                className="filter-select"
                                value={hourlyDate}
                                onChange={(e) => setHourlyDate(e.target.value)}
                                style={{ fontSize: 12, minWidth: 130 }}
                            />
                        )}
                    </div>
                </div>
                {!hourlyStore ? (
                    <div className="empty-state"><p>Select a store to view hourly images</p></div>
                ) : hourlyLoading ? (
                    <LoadingSpinner message="Loading images..." />
                ) : hourlyImages && hourlyImages.times && hourlyImages.times.length > 0 ? (
                    <div>
                        {hourlyImages.times.map((t) => (
                            <div key={t.time} style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 }}>
                                    {t.displayTime} ({t.cameras.length} camera{t.cameras.length !== 1 ? "s" : ""})
                                </div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {t.cameras.map((c, ci) => (
                                        <img
                                            key={ci}
                                            src={c.url}
                                            alt={`Cam ${c.camera}`}
                                            style={{
                                                width: 160, height: 120, objectFit: "cover",
                                                borderRadius: 6, cursor: "pointer",
                                                border: "1px solid var(--border)"
                                            }}
                                            onClick={() => {
                                                const allImgs = hourlyImages.times.flatMap(tt =>
                                                    tt.cameras.map(cc => ({ url: cc.url, label: `${tt.displayTime} - Cam ${cc.camera}` }))
                                                );
                                                const idx = allImgs.findIndex(img => img.url === c.url);
                                                setModalImages(allImgs);
                                                setModalIndex(idx >= 0 ? idx : 0);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="empty-state"><p>No images for this date</p></div>
                )}
            </div>

            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
            </>)}
        </div>
    );
}

export default Dashboard;
